import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAgency } from '@/contexts/AgencyContext';
import { ShieldAlert, Snowflake, Lock } from 'lucide-react';

/**
 * Tier 4c — surfaces moderation state to agency users so they understand
 * why an action is suddenly unavailable (uploads blocked, edits blocked,
 * trial paused). Renders nothing for a healthy agency.
 */
export function ModerationBanner() {
  const { agencyId } = useAgency();
  const { data } = useQuery({
    queryKey: ['moderation-state', agencyId],
    enabled: !!agencyId,
    staleTime: 30_000,
    queryFn: async () => {
      const { data } = await supabase
        .from('agencies')
        .select('suspended_at,suspended_reason,read_only_at,read_only_reason,upload_freeze_at,upload_freeze_reason,subscription_status')
        .eq('id', agencyId!)
        .maybeSingle();
      return data;
    },
  });

  if (!data) return null;

  const flag =
    data.suspended_at
      ? { icon: ShieldAlert, label: 'Account suspended', reason: data.suspended_reason, tone: 'destructive' as const }
      : data.read_only_at
      ? { icon: Lock, label: 'Read-only mode', reason: data.read_only_reason, tone: 'warning' as const }
      : data.upload_freeze_at
      ? { icon: Snowflake, label: 'Uploads frozen', reason: data.upload_freeze_reason, tone: 'warning' as const }
      : null;

  if (!flag) return null;

  const cls =
    flag.tone === 'destructive'
      ? 'bg-destructive/10 text-destructive border-destructive/30'
      : 'bg-amber-50 text-amber-800 border-amber-200';

  return (
    <div className={`px-4 py-2 text-xs flex items-center gap-2 border-b ${cls}`}>
      <flag.icon className="h-3.5 w-3.5 shrink-0" />
      <span className="font-semibold">{flag.label}.</span>
      <span className="truncate">{flag.reason || 'Contact support to restore full access.'}</span>
    </div>
  );
}
